import { TvvBesteldeRoute } from "@/entities/tvv";
import { TvvBus } from "@/entities/tvv/domain/tvv-bestelde-route.ts";
import { AfgeschaaldeBussen } from "@/features/tvv/components/shared/afgeschaalde-bussen.tsx";
import { Button, Icon } from "@/shared/components/ui";
import { formatDateTimeRelative } from "@/shared/utils";
import { useState } from "react";
import { EtaDialog } from "./eta-dialog";

type StationEtaSectionProps = {
    stationCode: string;
    stationNaam: string;
    aantalBussen: number;
    aantalBussenStation: number;
    bussen: TvvBus[];
    route: TvvBesteldeRoute;
    dossierId: string;
    isRouteComplete: boolean;
    canAddOrEditEtas: boolean;
    ["data-testid"]?: string;
};

export function StationEtaSection({
    stationCode,
    stationNaam,
    aantalBussen,
    aantalBussenStation,
    bussen,
    route,
    dossierId,
    canAddOrEditEtas,
    "data-testid": dataTestId,
}: Readonly<StationEtaSectionProps>) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedBus, setSelectedBus] = useState<TvvBus | undefined>(undefined);
    const [actieveBussen, afgeschaaldeBussen] = TvvBesteldeRoute.actieveAndAfgeschaaldeBussen({ bussen });
    const totaalActieveBussen = TvvBesteldeRoute.actieveAndAfgeschaaldeBussen(route)[0].length;

    return (
        <div className="space-y-2" data-testid={dataTestId}>
            <div className="text-content-subtle text-s font-bold">
                {stationNaam} ({actieveBussen.length}/{aantalBussenStation})
            </div>

            {bussen.length === 0 ? (
                <div className="text-content-subtle text-s">Er zijn nog geen ETA's voor {stationNaam} bekend</div>
            ) : (
                <div className="space-y-2">
                    {actieveBussen.map((bus, index) => (
                        <div
                            key={`eta-${index}-${bus.tijd.getTime()}`}
                            className={`text-s flex items-center gap-2 py-1 ${bussen.length !== index + 1 && "border-b"}`}
                            data-testid={`bus-eta-${index + 1}`}
                        >
                            <Icon name="bus" className="text-content-emphasis h-6 w-6" />
                            <span className="text-content-emphasis pl-2 font-medium">{index + 1}</span>
                            <span className="text-content-subtle text-s ml-auto" data-testid="eta-tijd">
                                ETA {formatDateTimeRelative(bus.tijd)}
                            </span>
                            {canAddOrEditEtas && (
                                <Button
                                    icon="edit"
                                    iconOnly
                                    size="s"
                                    variant="tertiary"
                                    onClick={() => {
                                        setSelectedBus(bus);
                                        setDialogOpen(true);
                                    }}
                                    className="text-content-subtle"
                                    title="ETA wijzigen"
                                />
                            )}
                        </div>
                    ))}
                    <AfgeschaaldeBussen bussen={afgeschaaldeBussen} />
                </div>
            )}

            {totaalActieveBussen < aantalBussen && canAddOrEditEtas && (
                <Button
                    variant="secondary"
                    size="s"
                    icon="plus"
                    onClick={() => {
                        setSelectedBus(undefined);
                        setDialogOpen(true);
                    }}
                    className="mt-2"
                >
                    ETA toevoegen
                </Button>
            )}

            {dialogOpen && (
                <EtaDialog
                    open={dialogOpen}
                    setOpen={(open) => {
                        setDialogOpen(open);
                        if (!open) setSelectedBus(undefined);
                    }}
                    stationCode={stationCode}
                    stationNaam={stationNaam}
                    routeId={route.id}
                    selectedBus={selectedBus}
                    dossierId={dossierId}
                />
            )}
        </div>
    );
}
